/**
 * Purpose: Clamps long rich-text content to a short preview with a Read more / Read less
 *          toggle (CSH-592, collection descriptions).
 *
 * Public API: <read-more-component data-expanded="false"> with refs `content` (the clamped
 *             container) and `toggleButton` (wired via on:click="/toggle"). Markup lives in
 *             snippets/collection-description.liquid; styles in assets/main-collection-custom.css.
 *
 * Upstream deps: @theme/component (Component base class, refs, declarative events).
 *
 * Downstream consumers: snippets/collection-description.liquid.
 *
 * Failure modes: purely presentational. The collapsed state is server-rendered to avoid layout
 *                shift; if this script fails to load the preview stays clamped and the button is
 *                inert. Sets `data-fits` when the content is shorter than the preview so the
 *                button and fade are hidden. Focus moving into the clipped area expands it, so
 *                links in the preview are never focused while invisible.
 *
 * Performance: one ResizeObserver per instance; no layout reads outside its callback and clicks.
 */
import { Component } from '@theme/component';

/**
 * @typedef {Object} ReadMoreRefs
 * @property {HTMLElement} content - The clamped content container
 * @property {HTMLButtonElement} toggleButton - The Read more / Read less button
 */

/**
 * @extends {Component<ReadMoreRefs>}
 */
class ReadMoreComponent extends Component {
  requiredRefs = ['content', 'toggleButton'];

  /**
   * @type {ResizeObserver | undefined}
   */
  #resizeObserver;

  get #expanded() {
    return this.dataset.expanded === 'true';
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver = new ResizeObserver(this.#updateFits);
    this.#resizeObserver.observe(this.refs.content);
    this.addEventListener('focusin', this.#handleFocusIn);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver?.observe(this.refs.content);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.removeEventListener('focusin', this.#handleFocusIn);
  }

  /**
   * Marks the component as fitting when the content is no taller than the collapsed preview.
   */
  #updateFits = () => {
    if (this.#expanded) return;

    const { content } = this.refs;
    this.toggleAttribute('data-fits', content.scrollHeight <= content.clientHeight + 1);
  };

  /**
   * Expands when focus lands inside the clipped content, e.g. tabbing to a link in the preview.
   *
   * @param {FocusEvent} event
   */
  #handleFocusIn = (event) => {
    if (
      !this.#expanded &&
      event.target instanceof Node &&
      this.refs.content.contains(event.target)
    ) {
      this.#setExpanded(true);
    }
  };

  /**
   * Toggles between the clamped preview and the full content.
   */
  toggle = () => {
    const expanded = !this.#expanded;

    this.#setExpanded(expanded);

    // Collapsing can move the button above the viewport after reading a long description.
    if (!expanded) this.refs.toggleButton.scrollIntoView({ block: 'nearest' });
  };

  /**
   * @param {boolean} expanded
   */
  #setExpanded(expanded) {
    this.dataset.expanded = String(expanded);
    this.refs.toggleButton.setAttribute('aria-expanded', String(expanded));

    if (!expanded) this.#updateFits();
  }
}

if (!customElements.get('read-more-component')) {
  customElements.define('read-more-component', ReadMoreComponent);
}
